import { EditorialPageLayout } from '@/components/seo/EditorialPageLayout';

export default function DigitalGratitude() {
  return (
    <EditorialPageLayout
      path="/digital-gratitude"
      ogImage="/og/digital-gratitude.jpg"
      title="Digital Gratitude — Saying Thank You, On Purpose"
      metaDescription="Digital gratitude is the practice of using everyday tools to say thank you with intention. Learn why it matters, how it works, and how to begin a small practice today."
      eyebrow="A small practice"
      h1="Digital gratitude."
      lede="A quiet practice of using the tools you already have — your phone, your camera, your words — to tell people, specifically, what they have meant to you."
      articleSchema={{
        headline: 'Digital gratitude: saying thank you, on purpose',
        description:
          'A guide to digital gratitude — what it is, why it matters, and how to begin a small, sustainable practice of expressing thanks through the tools you already use every day.',
      }}
      related={[
        { label: 'Affirmations and emotional wellness', href: '/affirmations-and-emotional-wellness', hint: 'The other half of the practice — turning it inward.' },
        { label: 'Thank you message for parents', href: '/thank-you-message-for-parents', hint: 'Examples to start from.' },
        { label: 'What is a Living Announcement?', href: '/what-is-a-living-announcement', hint: 'The format gratitude takes when it gets serious.' },
      ]}
    >
      <p>
        Gratitude research is unusually consistent for a soft science. People
        who regularly express thanks — out loud, in writing, to actual humans —
        report higher life satisfaction, better sleep, lower stress, and closer
        relationships. The numbers are not small. The catch is that almost
        nobody actually does it.
      </p>

      <p>
        <strong>Digital gratitude</strong> is the practice of fixing that with
        the tools already in your pocket. Not as a productivity hack. Not as
        a wellness aesthetic. As a quiet, repeatable thing you do on purpose.
      </p>

      <h2>Why most gratitude stays in your head</h2>
      <p>
        Because the moment passes. You think it on the walk home and lose it
        by the time you sit down. Or you mean to send it as a text and then
        worry it will be weird. Or you save it for their birthday and then
        write something generic instead.
      </p>

      <p>
        Digital gratitude solves a small but stubborn logistics problem: <em>
        getting the specific words to the specific person while you still
        mean them in that exact way</em>.
      </p>

      <h2>What a practice looks like</h2>
      <ul>
        <li>
          <strong>One person, once a week.</strong> Not a list. Not a
          broadcast. One human, one specific thing.
        </li>
        <li>
          <strong>Be embarrassingly specific.</strong> Not "you're amazing."
          The exact thing they did. The sentence they said. The afternoon
          they showed up.
        </li>
        <li>
          <strong>Send it in a form they can return to.</strong> A reply in
          a chat thread vanishes. A{' '}
          <a href="/what-is-a-living-announcement">Living Announcement</a>{' '}
          does not.
        </li>
      </ul>

      <h2>It is not performance</h2>
      <p>
        Digital gratitude is private by default. It is not a post. It is not
        for the algorithm. The recipient is the audience. That is the whole
        point — and the reason it works.
      </p>

      <blockquote>
        Celebrate people while they're alive. Especially the ones you
        already take for granted.
      </blockquote>

      <h2>Where to start this week</h2>
      <p>
        Think of one person whose effort you have noticed and not named.
        Write three sentences about a specific thing they did. Send it in a
        form that lasts. Notice what happens — to them, and to you.
      </p>

      <p>
        That is the practice. There is nothing else to it.
      </p>
    </EditorialPageLayout>
  );
}
